<template>
    <div class="templates-gallery" :class="{ embedded }">
        <div class="header">
            <h3 v-if="!embedded" class="title">{{ $t("tables.templates.sidebarTitle") }}</h3>
            <a-input-search
                v-model="searchText"
                :placeholder="$t('tables.templates.searchPlaceholder')"
                class="search-input"
                style="width: 280px;"
            />
        </div>

        <div class="content">
            <a-spin :spinning="loading" wrapperClassName="content-spin">
                <div class="sector-tabs">
                    <button
                        v-for="sector in availableSectors"
                        :key="sector.key"
                        class="sector-tab"
                        :class="{ active: activeSector === sector.key }"
                        @click="activeSector = sector.key"
                    >
                        <i v-if="sector.icon" class="material-icons">{{ sector.icon }}</i>
                        <span>{{ sector.label }}</span>
                        <span class="count">({{ sector.count }})</span>
                    </button>
                </div>

                <div v-if="filteredTemplates.length === 0" class="empty-state">
                    <i class="material-icons">search_off</i>
                    <p>{{ searchText ? $t("tables.templates.noResults") : $t("tables.templates.noTemplatesInSector") }}</p>
                </div>

                <div v-else class="template-list">
                    <div
                        v-for="tpl in filteredTemplates"
                        :key="tpl.key"
                        class="template-list-item"
                        @click="openPreview(tpl)"
                    >
                        <div class="template-list-item-inner-wrapper">
                            <div class="block-template">
                                <div>
                                    <div class="icon-tile" :style="iconTileStyle(tpl.sector)">
                                        <i class="material-icons" :style="{ color: iconColor(tpl.sector) }">{{ tpl.icon || "dashboard_customize" }}</i>
                                    </div>
                                    <div class="title">{{ tpl.name }}</div>
                                    <div class="short-description">{{ tpl.description }}</div>
                                    <div class="card-meta">
                                        <span
                                            class="meta-chip"
                                            v-tippy="{ content: $t('tables.templates.tableCountTooltip', { count: tpl.tables.length }) }"
                                        >
                                            <i class="material-icons">table_chart</i>
                                            {{ tpl.tables.length }} {{ $t("tables.templates.tablesCountSuffix") }}
                                        </span>
                                        <span
                                            v-if="tpl.relations.length > 0"
                                            class="meta-chip"
                                            v-tippy="{ content: $t('tables.templates.relationsCountTooltip', { count: tpl.relations.length }) }"
                                        >
                                            <i class="material-icons">hub</i>
                                            {{ tpl.relations.length }} {{ $t("tables.templates.relationsCountSuffix") }}
                                        </span>
                                        <span
                                            v-if="tpl.hasDemoData"
                                            class="meta-chip demo"
                                            v-tippy="{ content: $t('tables.templates.demoDataTooltip') }"
                                        >
                                            <i class="material-icons">science</i>
                                            {{ $t("tables.templates.demoDataChip") }}
                                        </span>
                                    </div>
                                </div>
                                <div class="card-action">
                                    <a-button type="primary" ghost @click.stop="openPreview(tpl)">
                                        {{ $t("tables.templates.useTemplate") }}
                                    </a-button>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </a-spin>
        </div>

        <Modal2
            v-if="previewVisible"
            xlarge
            :showFooter="false"
            :title="selectedTemplate ? selectedTemplate.name : $t('tables.templates.previewTitle')"
            @cancel="closePreview"
        >
            <TemplatePreview
                v-if="selectedTemplate"
                :templateKey="selectedTemplate.key"
                @applied="onApplied"
                @cancel="closePreview"
            />
        </Modal2>
    </div>
</template>

<script>
import { TABLE_TEMPLATE_API } from "../../../../api/tableTemplate";
import Modal2 from "../../../../components/common/Modal2.vue";
import TemplatePreview from "./TemplatePreview.vue";
import { mapActions } from "vuex";

const SECTOR_ICONS = {
    personel: "groups",
    teknoloji: "memory",
    finans: "account_balance",
    saglik: "local_hospital",
    egitim: "school",
    sigorta: "verified_user",
    eticaret: "shopping_cart",
    perakende: "storefront",
    uretim: "factory",
    yapi: "construction",
    market: "local_grocery_store",
    yiyecek: "restaurant",
    pazarlama: "campaign",
    lojistik: "local_shipping",
    diger: "more_horiz",
};

// Her sektör için soft pastel bg + canlı icon rengi
const SECTOR_COLORS = {
    personel:   { bg: "#e6f4ff", color: "#1890ff" },   // mavi
    teknoloji:  { bg: "#f3e8ff", color: "#7c3aed" },   // mor
    finans:     { bg: "#dcfce7", color: "#16a34a" },   // yeşil
    saglik:     { bg: "#fee2e2", color: "#dc2626" },   // kırmızı
    egitim:     { bg: "#fef3c7", color: "#d97706" },   // turuncu/sarı
    sigorta:    { bg: "#ccfbf1", color: "#0d9488" },   // teal
    eticaret:   { bg: "#fef9c3", color: "#ca8a04" },   // amber
    perakende:  { bg: "#cffafe", color: "#0891b2" },   // cyan
    uretim:     { bg: "#e5e7eb", color: "#4b5563" },   // gri
    yapi:       { bg: "#fed7aa", color: "#c2410c" },   // turuncu
    market:     { bg: "#d9f99d", color: "#65a30d" },   // lime
    yiyecek:    { bg: "#ffedd5", color: "#ea580c" },   // turuncu-kırmızı
    pazarlama:  { bg: "#fce7f3", color: "#db2777" },   // pembe
    lojistik:   { bg: "#e0e7ff", color: "#4338ca" },   // indigo
    diger:      { bg: "#f3f4f6", color: "#6b7280" },   // nötr gri
};

export default {
    name: "TemplatesGallery",
    components: { Modal2, TemplatePreview },
    props: {
        // Modal içinde embed edildiğinde title gizlenir, applied event'i parent'e iletilir.
        embedded: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            loading: false,
            templates: [],
            searchText: "",
            activeSector: "all",
            previewVisible: false,
            selectedTemplate: null,
        };
    },
    computed: {
        availableSectors() {
            const counts = {};
            for (const t of this.templates) {
                const k = t.sector || "diger";
                counts[k] = (counts[k] || 0) + 1;
            }
            const sectors = [
                {
                    key: "all",
                    label: this.$t("tables.templates.allSector"),
                    icon: "apps",
                    count: this.templates.length,
                },
            ];
            for (const [key, count] of Object.entries(counts)) {
                sectors.push({
                    key,
                    label: this.sectorLabel(key),
                    icon: SECTOR_ICONS[key] || "label",
                    count,
                });
            }
            return sectors;
        },
        filteredTemplates() {
            let list = this.templates;
            if (this.activeSector !== "all") {
                list = list.filter((t) => (t.sector || "diger") === this.activeSector);
            }
            if (this.searchText) {
                const q = this.searchText.toLowerCase();
                list = list.filter(
                    (t) =>
                        (t.name || "").toLowerCase().includes(q) ||
                        (t.description || "").toLowerCase().includes(q)
                );
            }
            return list;
        },
    },
    methods: {
        ...mapActions({
            syncTables: "table/syncTables",
            syncModules: "module/syncModules",
        }),
        sectorLabel(key) {
            const i18nKey = `tables.templates.sectors.${key}`;
            const translated = this.$t(i18nKey);
            // vue-i18n anahtarı bulamazsa anahtarın kendisini döner; o durumda raw key göster
            return translated === i18nKey ? key : translated;
        },
        iconTileStyle(sector) {
            const c = SECTOR_COLORS[sector] || SECTOR_COLORS.diger;
            return { background: c.bg };
        },
        iconColor(sector) {
            const c = SECTOR_COLORS[sector] || SECTOR_COLORS.diger;
            return c.color;
        },
        async fetchTemplates() {
            this.loading = true;
            try {
                this.templates = await TABLE_TEMPLATE_API.list();
            } catch (e) {
                console.error(this.$t("tables.templates.loadFailed"), e);
                this.$message.error(this.$t("tables.templates.loadFailed"));
            } finally {
                this.loading = false;
            }
        },
        openPreview(tpl) {
            this.selectedTemplate = tpl;
            this.previewVisible = true;
        },
        closePreview() {
            this.previewVisible = false;
            this.selectedTemplate = null;
        },
        async onApplied(result) {
            this.$message.success(
                this.$t("tables.templates.applySuccess", {
                    name: result.template.name,
                    tables: result.tables.length,
                    relations: result.relations.length,
                    records: result.demoRecordCount,
                })
            );
            this.closePreview();
            await Promise.all([this.syncTables(0), this.syncModules()]);
            this.$emit("applied", result);
            this.$router.push({ path: "/tables", query: { type: "all-tables" } });
        },
    },
    created() {
        this.fetchTemplates();
    },
};
</script>

<style scoped lang="scss">
.templates-gallery {
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0; // flex child overflow için
    padding: 10px;
    gap: 10px;
    overflow: hidden;

    .header {
        flex-shrink: 0;
    }

    .content {
        flex: 1;
        min-height: 0;
        overflow-y: auto;
        padding-right: 6px;
    }

    // Modal içinde embed edildiğinde sabit yükseklik + iç scroll
    &.embedded {
        padding: 0;
        height: auto;
        max-height: 60vh;

        .content {
            max-height: calc(60vh - 60px);
        }
    }

    .header {
        display: flex;
        justify-content: space-between;
        align-items: center;

        .title {
            font-size: 18px;
            font-weight: 600;
            margin: 0;
        }
    }

    .content {
        display: flex;
        flex-direction: column;
        gap: 15px;
    }

    .sector-tabs {
        display: flex;
        gap: 6px;
        flex-wrap: wrap;
        padding-bottom: 5px;

        .sector-tab {
            display: inline-flex;
            align-items: center;
            gap: 6px;
            padding: 6px 12px;
            background: #fafafa;
            border: 1px solid #e8e8e8;
            border-radius: 16px;
            font-size: 13px;
            color: #555;
            cursor: pointer;
            transition: all 0.15s ease;

            .material-icons {
                font-size: 16px;
            }

            .count {
                color: #999;
                font-size: 12px;
            }

            &:hover {
                background: #f0f0f0;
                border-color: #d9d9d9;
            }

            &.active {
                background: #1890ff;
                border-color: #1890ff;
                color: white;

                .count {
                    color: rgba(255, 255, 255, 0.85);
                }
            }
        }
    }

    .template-list {
        display: grid;
        grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
        gap: 14px;
        align-items: stretch;

        .template-list-item {
            display: flex;

            .template-list-item-inner-wrapper {
                cursor: pointer;
                border: 1px solid #dbe1e8;
                box-shadow: 0 1px 8px rgb(0 0 0 / 3%);
                border-radius: 6px;
                height: 100%;
                min-height: 200px;
                overflow: hidden;
                width: 100%;
                background: #ffffff;
                transition: border-color 0.15s ease, box-shadow 0.15s ease;

                &:hover {
                    border-color: #008aff;
                    box-shadow: 0 2px 12px rgba(24, 144, 255, 0.08);
                }

                .block-template {
                    display: flex;
                    flex-direction: column;
                    height: 100%;
                    justify-content: flex-start;
                    padding: 16px 16px 14px;
                    width: 100%;
                    gap: 10px;

                    .icon-tile {
                        width: 44px;
                        height: 44px;
                        border-radius: 8px;
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        margin-bottom: 6px;
                        // background ve icon rengi sektöre göre inline style ile veriliyor

                        .material-icons {
                            font-size: 24px;
                        }
                    }

                    .title {
                        font-size: 15px;
                        font-weight: 600;
                        line-height: 19px;
                        color: #222;
                        display: -webkit-box;
                        -webkit-line-clamp: 1;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        word-wrap: break-word;
                    }

                    .short-description {
                        font-size: 13px;
                        line-height: 18px;
                        color: #516173;
                        display: -webkit-box;
                        -webkit-line-clamp: 2;
                        -webkit-box-orient: vertical;
                        overflow: hidden;
                        text-overflow: ellipsis;
                        word-wrap: break-word;
                    }

                    .card-meta {
                        display: flex;
                        gap: 5px;
                        flex-wrap: wrap;

                        .meta-chip {
                            display: inline-flex;
                            align-items: center;
                            gap: 4px;
                            padding: 3px 8px;
                            background: #f5f5f5;
                            border-radius: 10px;
                            font-size: 11px;
                            color: #555;
                            line-height: 1.3;

                            .material-icons {
                                font-size: 13px;
                            }

                            &.demo {
                                background: #fff7e6;
                                color: #d46b08;
                            }
                        }
                    }

                    .card-action {
                        display: flex;
                        margin-top: auto;
                        padding-top: 10px;

                        ::v-deep .ant-btn {
                            height: 30px;
                            padding: 0 12px;
                            font-size: 13px;
                            line-height: 28px;
                        }
                    }
                }
            }
        }
    }

    .empty-state {
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 8px;
        padding: 60px 20px;
        color: #999;

        .material-icons {
            font-size: 48px;
        }

        p {
            margin: 0;
            font-size: 14px;
        }
    }
}
</style>
